import { z } from 'zod/v4-mini';

import { FileModel, fileSchema } from '@/models/FileModel';
import { Schema, cz } from '@/utils/zodUtils.ts';

import {
  TeleMonitoringCriteria,
  telemonitoringCriteriaSchema,
} from './TelemonitoringCriteriaModel';

export const telemonitoringTierSchema = z.literal(['base', 'level1', 'level2']);
export type TelemonitoringTier = z.infer<typeof telemonitoringTierSchema>;
export const renewalSuggestionSchema = z.literal(['expiring', 'add_insulin']);
export type RenewalSuggestion = z.infer<typeof renewalSuggestionSchema>;
export const telemonitoringTagsSchema = z.literal([
  'active',
  'expiring_soon',
  'expired',
  'expired_recently',
  'gestational_no_insulin',
  'no_glycemia_data',
  'nurse_asked_for_reinclusion',
]);
export type TelemonitoringTags = z.infer<typeof telemonitoringTagsSchema>;
export const cannotRenewReasonSchema = z.literal([
  'has_upcoming',
  'has_careplan_until_term',
]);
export type CannotRenewReason = z.infer<typeof cannotRenewReasonSchema>;
export const patientRenewalDecisionSchema = z.literal([
  'do_not_renew',
  'decide_later',
]);
export type PatientRenewalDecision = z.infer<
  typeof patientRenewalDecisionSchema
>;

export type CarePlan = {
  id: string;
  periodStart: string;
  periodEnd: string;
  description?: string;
  descriptionAttachment?: FileModel;
  telemonitoringTier: TelemonitoringTier;
  telemonitoringCriteria?: TeleMonitoringCriteria;
  prescription?: FileModel;
  subject: string;
};

export const carePlanSchema: Schema<CarePlan> = z.object({
  id: z.string(),
  periodStart: z.iso.date(),
  periodEnd: z.iso.date(),
  description: cz.optional(z.string()),
  descriptionAttachment: cz.optional(fileSchema),
  telemonitoringCriteria: cz.optional(telemonitoringCriteriaSchema),
  telemonitoringTier: telemonitoringTierSchema,
  prescription: cz.optional(fileSchema),
  subject: z.string(),
});

export type CarePlans = {
  current_care_plan?: CarePlan;
  can_renew_care_plan: boolean;
  cannot_renew_reason?: CannotRenewReason;
  renewal_suggestion?: RenewalSuggestion;
  telemonitoring_tags: TelemonitoringTags[];
};

export const carePlansSchema: Schema<CarePlans> = z.object({
  current_care_plan: cz.optional(carePlanSchema),
  can_renew_care_plan: z.boolean(),
  cannot_renew_reason: cz.optional(cannotRenewReasonSchema),
  renewal_suggestion: cz.optional(renewalSuggestionSchema),
  telemonitoring_tags: cz.forcedArray(telemonitoringTagsSchema),
});

export const getTelemonitoringTierTranslation = (tier: TelemonitoringTier) => {
  return {
    base: 'diabetesForms.criteriaBase',
    level1: 'diabetesForms.criteriaLevel1',
    level2: 'diabetesForms.criteriaLevel2',
  }[tier];
};
